/**
 * Abonnement aux notifications Web Push : permission, souscription (clé VAPID
 * publique), persistance dans PocketBase (`push_subscriptions`). L'envoi est fait
 * par le sidecar `notifier`.
 */
import { useCallback, useEffect, useState } from 'react';
import { currentUserId, pb } from '@/lib/pocketbase';
import type { PushSubscriptionRecord } from '@/types/records';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';

/** Convertit une clé VAPID base64url en Uint8Array (format attendu par subscribe). */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export interface PushState {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  busy: boolean;
  error: string;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export function usePush(): PushState {
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY;

  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Abonnement navigateur déjà présent ?
  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => setSubscribed(false));
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    setError('');
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Permission refusée');

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const json = sub.toJSON();
      const uid = currentUserId();
      // Upsert par endpoint (index unique) : évite les doublons multi-appareils.
      const existing = await pb
        .collection('push_subscriptions')
        .getFullList<PushSubscriptionRecord>({
          filter: `endpoint = ${JSON.stringify(sub.endpoint)}`,
        })
        .catch(() => []);
      const payload = {
        user: uid,
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      };
      if (existing.length) {
        await pb.collection('push_subscriptions').update(existing[0]!.id, payload);
      } else {
        await pb.collection('push_subscriptions').create(payload);
      }
      setSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Abonnement impossible');
      console.error(err);
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    setError('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const records = await pb
          .collection('push_subscriptions')
          .getFullList<PushSubscriptionRecord>({
            filter: `endpoint = ${JSON.stringify(sub.endpoint)}`,
          })
          .catch(() => []);
        await Promise.all(
          records.map((r) =>
            pb
              .collection('push_subscriptions')
              .delete(r.id)
              .catch(() => {}),
          ),
        );
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Désabonnement impossible');
      console.error(err);
    } finally {
      setBusy(false);
    }
  }, [supported]);

  return { supported, permission, subscribed, busy, error, subscribe, unsubscribe };
}
